import { AnimatePresence, motion } from "framer-motion"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"

import useAppStore from "../../../store/useAppStore"
import EmployeeDayLogTimeEditPopover from "./EmployeeDayLogTimeEditPopover"
import {
    dayHasAttendanceMark,
    calculateEffectiveWorkedMinutes,
    getEmployeeDayCalendarTone,
    getLogsForCalendarDay,
    sortEmployeeDayLogs
} from "../../../utils/employeeDayEffectiveMinutes"
import { formatMonthWorkHoursMinutes } from "../../../utils/employeeMonthWorkSummary"
import {
    buildMonthCalendarDays,
    getIsoWeekNumber,
    isCalendarDayFuture,
    isSameCalendarMonthAsToday
} from "../../../utils/monthCalendarGrid"
import {
    formatAttendanceTypeCell,
    formatLogConfirmerCell,
    formatLogEditorCell,
    formatLogLocationCell,
    LogConfirmationStatusIcon
} from "./employeeDayLogTableCells"

/** Boje ćelija = efektivno radno vreme vs ~7,5h (vidi employeeDayEffectiveMinutes). */
const CALENDAR_LEGEND_TONES = [
    { tone: "exact", key: "exactExit" },
    { tone: "over", key: "lateExit" },
    { tone: "short", key: "earlyExit" },
    { tone: "absent", key: "absent" }
]

const yearDropdownValues = () => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: 6 }, (_, i) => (i === 0 ? currentYear + 1 : currentYear - (i - 1)))
}

const formatSelectedDateLabel = (day) =>
    `${String(day.date).padStart(2, "0")}.${String(day.month + 1).padStart(2, "0")}.${day.year}`

const getLogRowKey = (logsTableKey, log, idx) =>
    log._id ? `${logsTableKey}-${log._id}` : `${logsTableKey}-${idx}-${log.vreme}-${log.status}`

const LOG_STATUS_ARROW = {
    ulaz: { color: "#16a34a", labelKey: "dayLogs.statusEntry", path: "M5 12h12M13 8l4 4-4 4" },
    izlaz: { color: "#dc2626", labelKey: "dayLogs.statusExit", path: "M19 12H7M11 8l-4 4 4 4" }
}

const LOG_ROW_FLASH_MS = 520

const LogStatusIcon = ({ status }) => {
    const { t } = useTranslation("stats")
    const cfg = LOG_STATUS_ARROW[status]
    if (!cfg) {
        const text = status && String(status).trim() ? status : "—"
        return <span>{text}</span>
    }
    const label = t(cfg.labelKey)
    return (
        <span className="asv-emp-day-log-status-icon" title={label} aria-label={label}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
                <path
                    d={cfg.path}
                    fill="none"
                    stroke={cfg.color}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    )
}

/**
 * Mesec/godina dropdown + mesečna mreža (pon–ned) za admin modal mesecnih logova.
 */
const EmployeeMonthLogsCalendar = ({ isDarkTheme, selectedDay, onSelectDay, modalContainerRef }) => {
    const { t } = useTranslation("stats")
    const months = t("months", { returnObjects: true })
    const weekdayLabels = t("weekdaysShort", { returnObjects: true })
    const { year, month } = useAppStore((state) => state.employeeMonthLogsModalDateFilter)
    const setEmployeeMonthLogsModalDateMonth = useAppStore((state) => state.setEmployeeMonthLogsModalDateMonth)
    const setEmployeeMonthLogsModalDateYear = useAppStore((state) => state.setEmployeeMonthLogsModalDateYear)
    const employeeMonthLogsByDay = useAppStore((state) => state.employeeMonthLogsByDay)
    const loadingEmployeeMonthLogs = useAppStore((state) => state.loadingEmployeeMonthLogs)
    const employeeMonthLogsModalUserId = useAppStore((state) => state.employeeMonthLogsModalUserId)
    const updateEmployeeDayLogTime = useAppStore((state) => state.updateEmployeeDayLogTime)
    const employeesData = useAppStore((state) => state.employeesData)

    const [isMonthOpen, setIsMonthOpen] = useState(false)
    const [isYearOpen, setIsYearOpen] = useState(false)
    const [editingLogRowKey, setEditingLogRowKey] = useState(null)
    /** Tooltip broja nedelje ide u portal (fixed) — u modalu ga preci iscrtavaju ispod kalendara. */
    const [weekTooltip, setWeekTooltip] = useState(null)

    const { calendarDays, numberOfRows } = useMemo(
        () => buildMonthCalendarDays(year, month),
        [year, month]
    )

    const gridRows = useMemo(
        () => Array.from({ length: numberOfRows }, () => "minmax(24px, 1fr)").join(" "),
        [numberOfRows]
    )

    const weekNumbers = useMemo(
        () =>
            Array.from({ length: numberOfRows }, (_, row) => {
                const firstDayOfRow = calendarDays[row * 7]
                if (!firstDayOfRow) return null
                return getIsoWeekNumber(firstDayOfRow.year, firstDayOfRow.month, firstDayOfRow.date)
            }),
        [calendarDays, numberOfRows]
    )

    const showWeekTooltip = useCallback((event, weekNumber) => {
        if (weekNumber == null) return
        const rect = event.currentTarget.getBoundingClientRect()
        setWeekTooltip({
            weekNumber,
            top: rect.top + rect.height / 2,
            left: rect.right + 8
        })
    }, [])

    const hideWeekTooltip = useCallback(() => setWeekTooltip(null), [])

    useEffect(() => {
        setWeekTooltip(null)
    }, [year, month])

    const handleMonthChange = async (nextMonth) => {
        await setEmployeeMonthLogsModalDateMonth(nextMonth)
        setIsMonthOpen(false)
    }

    const handleYearChange = async (nextYear) => {
        await setEmployeeMonthLogsModalDateYear(nextYear)
        setIsYearOpen(false)
    }

    const years = useMemo(() => yearDropdownValues(), [])

    const dayMetaByKey = useMemo(() => {
        if (loadingEmployeeMonthLogs || employeeMonthLogsByDay == null) return null
        const map = new Map()
        for (const d of calendarDays) {
            if (!d.isCurrentMonth) continue
            const logs = getLogsForCalendarDay(employeeMonthLogsByDay, d.year, d.month, d.date)
            map.set(`${d.year}-${d.month}-${d.date}`, {
                tone: getEmployeeDayCalendarTone(logs),
                showAttendanceMark: dayHasAttendanceMark(logs)
            })
        }
        return map
    }, [calendarDays, employeeMonthLogsByDay, loadingEmployeeMonthLogs])

    const selectedLogs = useMemo(() => {
        if (!selectedDay) return []
        if (employeeMonthLogsByDay == null) return []
        const raw = getLogsForCalendarDay(employeeMonthLogsByDay, selectedDay.year, selectedDay.month, selectedDay.date)
        return sortEmployeeDayLogs(raw)
    }, [selectedDay, employeeMonthLogsByDay])

    const totalInsideFormatted = useMemo(() => {
        if (!selectedLogs.length) return null
        const minutes = calculateEffectiveWorkedMinutes(selectedLogs)
        return formatMonthWorkHoursMinutes(minutes ?? 0)
    }, [selectedLogs])

    const logsTableKey = selectedDay
        ? `${selectedDay.year}-${selectedDay.month}-${selectedDay.date}`
        : "none"

    const prevVremeByLogIdRef = useRef({})
    const [flashingLogId, setFlashingLogId] = useState(null)

    useEffect(() => {
        prevVremeByLogIdRef.current = {}
        setFlashingLogId(null)
    }, [logsTableKey])

    useEffect(() => {
        if (!selectedLogs.length) {
            prevVremeByLogIdRef.current = {}
            return
        }

        const prev = prevVremeByLogIdRef.current
        let changedLogId = null
        const next = {}

        for (const log of selectedLogs) {
            if (!log._id) continue
            const id = String(log._id)
            next[id] = log.vreme
            if (prev[id] != null && prev[id] !== log.vreme) {
                changedLogId = id
            }
        }

        prevVremeByLogIdRef.current = next

        if (!changedLogId) return

        setFlashingLogId(changedLogId)
        const timer = window.setTimeout(() => setFlashingLogId(null), LOG_ROW_FLASH_MS)
        return () => window.clearTimeout(timer)
    }, [selectedLogs])

    useEffect(() => {
        setEditingLogRowKey(null)
    }, [logsTableKey])

    const closeTimeEdit = useCallback(() => setEditingLogRowKey(null), [])

    const editingLogContext = useMemo(() => {
        if (!editingLogRowKey) return null
        for (let idx = 0; idx < selectedLogs.length; idx++) {
            const log = selectedLogs[idx]
            const rowKey = getLogRowKey(logsTableKey, log, idx)
            if (rowKey === editingLogRowKey) {
                return {
                    log,
                    prevVreme: idx > 0 ? selectedLogs[idx - 1].vreme : null,
                    nextVreme: idx < selectedLogs.length - 1 ? selectedLogs[idx + 1].vreme : null
                }
            }
        }
        return null
    }, [editingLogRowKey, selectedLogs, logsTableKey])

    const handleSaveLogTime = useCallback(
        (newVreme) => {
            const ctx = editingLogContext
            const userId = employeeMonthLogsModalUserId
            if (!ctx?.log?._id || !userId) return

            closeTimeEdit()

            void updateEmployeeDayLogTime({
                logId: ctx.log._id,
                newTime: newVreme
            })
        },
        [editingLogContext, employeeMonthLogsModalUserId, updateEmployeeDayLogTime, closeTimeEdit]
    )

    const viewingCurrentMonth = isSameCalendarMonthAsToday(year, month)

    const handleDayCellClick = useCallback(
        (day) => {
            if (!day.isCurrentMonth || !onSelectDay) return
            if (isCalendarDayFuture(day)) return
            onSelectDay({ year: day.year, month: day.month, date: day.date })
        },
        [onSelectDay]
    )

    const logsSectionDark = isDarkTheme ? " asv-emp-day-logs-section--dark" : ""

    return (
        <div className="asv-employee-month-logs-calendar">
            <div className="asv-employee-month-logs-calendar-inner">
                <div className="asv-employee-calendar-block">
                    <div className="asv-employee-calendar-filters cv-filters">
                        <div className="cv-filter-item">
                            <label className={isDarkTheme ? "dark-color" : ""}>{t("filters.month")}</label>
                            <div
                                className={`cv-dropdown asv-employee-calendar-filter-dropdown ${isDarkTheme ? "dark-dropdown" : ""}`}
                                onClick={() => setIsMonthOpen(!isMonthOpen)}
                            >
                                {months[month]}
                                <motion.div className="arrow">
                                    <svg viewBox="0 0 24 24" width="24" height="24" style={{ display: "block" }}>
                                        <path d="M7 10l5 5 5-5" fill="none" stroke={isDarkTheme ? "rgb(219, 219, 219)" : "black"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </motion.div>
                                <AnimatePresence>
                                    {isMonthOpen && (
                                        <motion.div
                                            className={`cv-dropdown-list ${isDarkTheme ? "dark-dropdownlist" : ""}`}
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.14, ease: "easeOut" }}
                                        >
                                            {months.map((monthName, idx) => (
                                                <div
                                                    key={monthName}
                                                    className={`cv-dropdown-item ${isDarkTheme ? "dark-dropdownitem" : ""}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleMonthChange(idx)
                                                    }}
                                                >
                                                    {monthName}
                                                </div>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                        <div className="cv-filter-item">
                            <label className={isDarkTheme ? "dark-color" : ""}>{t("filters.year")}</label>
                            <div
                                className={`cv-dropdown asv-employee-calendar-filter-dropdown ${isDarkTheme ? "dark-dropdown" : ""}`}
                                onClick={() => setIsYearOpen(!isYearOpen)}
                            >
                                {year}
                                <motion.div className="arrow">
                                    <svg viewBox="0 0 24 24" width="24" height="24" style={{ display: "block" }}>
                                        <path d="M7 10l5 5 5-5" fill="none" stroke={isDarkTheme ? "rgb(219, 219, 219)" : "black"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </motion.div>
                                <AnimatePresence>
                                    {isYearOpen && (
                                        <motion.div
                                            className={`cv-dropdown-list ${isDarkTheme ? "dark-dropdownlist" : ""}`}
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.14, ease: "easeOut" }}
                                        >
                                            {years.map((y) => (
                                                <div
                                                    key={y}
                                                    className={`cv-dropdown-item ${isDarkTheme ? "dark-dropdownitem" : ""}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleYearChange(y)
                                                    }}
                                                >
                                                    {y}
                                                </div>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>

                    <div className="asv-employee-calendar-slot">
                        <div
                            className={`asv-employee-calendar-grid-wrap cv-grid-container${isDarkTheme ? " asv-employee-calendar-grid-wrap--dark" : ""}`}
                        >
                            <div
                                className={`asv-emp-week-numbers${isDarkTheme ? " asv-emp-week-numbers--dark" : ""}`}
                                style={{ gridTemplateRows: `var(--asv-cal-head-h) ${gridRows}` }}
                                role="group"
                                aria-label={t("calendar.weekNumberAria")}
                            >
                                <span className="asv-emp-week-numbers-head" aria-hidden />
                                {weekNumbers.map((weekNumber, row) => (
                                    <span
                                        key={`${year}-${month}-week-${row}`}
                                        className="asv-emp-week-number"
                                        onMouseEnter={(e) => showWeekTooltip(e, weekNumber)}
                                        onMouseLeave={hideWeekTooltip}
                                    >
                                        {weekNumber ?? ""}
                                    </span>
                                ))}
                            </div>
                            <div
                                key={`${year}-${month}`}
                                className={`cv-calendar-grid ${isDarkTheme ? "dark-calendar-grid" : ""}`}
                                style={{ gridTemplateRows: `auto ${gridRows}` }}
                                data-viewing-current-month={viewingCurrentMonth || undefined}
                            >
                                {weekdayLabels.map((label) => (
                                    <div key={label} className="cv-day-name">
                                        {label}
                                    </div>
                                ))}
                                {calendarDays.map((day) => {
                                    const cellKey = `${day.year}-${day.month}-${day.date}`
                                    const isFutureDay = day.isCurrentMonth && isCalendarDayFuture(day)
                                    const dayMeta = dayMetaByKey?.get(cellKey)
                                    const tone = !isFutureDay ? dayMeta?.tone : null
                                    const toneClass = day.isCurrentMonth && tone ? `asv-emp-day--${tone}` : ""
                                    const attendanceMarkClass =
                                        !isFutureDay && day.isCurrentMonth && dayMeta?.showAttendanceMark
                                            ? "asv-emp-day-cell--attendance-mark"
                                            : ""
                                    const darkCellClass = isDarkTheme ? "asv-emp-day-cell--dark" : ""
                                    const isSelected =
                                        selectedDay != null &&
                                        selectedDay.year === day.year &&
                                        selectedDay.month === day.month &&
                                        selectedDay.date === day.date
                                    const selectedClass = isSelected ? "asv-emp-day-cell--selected" : ""
                                    const outsideMonthClass = !day.isCurrentMonth ? "asv-emp-day-cell--outside-month" : ""
                                    const futureClass = isFutureDay ? "asv-emp-day-cell--future" : ""
                                    return (
                                        <div
                                            key={cellKey}
                                            className={`cv-day asv-emp-day-cell ${darkCellClass} ${!day.isCurrentMonth ? "cv-day-other-month" : ""} ${outsideMonthClass} ${futureClass} ${toneClass} ${attendanceMarkClass} ${selectedClass}`.trim()}
                                            onClick={() => handleDayCellClick(day)}
                                            aria-disabled={!day.isCurrentMonth || isFutureDay || undefined}
                                        >
                                            {day.date}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                        <div
                            className={`asv-emp-calendar-legend ${isDarkTheme ? "asv-emp-calendar-legend--dark" : ""}`}
                            aria-label={t("calendar.legendAria")}
                        >
                            {CALENDAR_LEGEND_TONES.map(({ tone, key }) => (
                                <span key={tone} className="asv-emp-calendar-legend-item">
                                    <span
                                        className={`asv-emp-calendar-legend-swatch asv-emp-calendar-legend-swatch--${tone}`}
                                        aria-hidden
                                    />
                                    <span className="asv-emp-calendar-legend-label">{t(`calendar.${key}`)}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={`asv-emp-day-logs-section${logsSectionDark}`}>
                    <div className={`asv-emp-day-logs-section-title ${isDarkTheme ? "dark-color" : ""}`}>
                        {selectedDay
                            ? t("dayLogs.logsForDate", { date: formatSelectedDateLabel(selectedDay) })
                            : t("dayLogs.selectDay")}
                    </div>
                    <div className="asv-emp-day-logs-table-host">
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.div
                                key={logsTableKey}
                                className="asv-emp-day-logs-table-animated"
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                transition={{ duration: 0.16, ease: "easeOut" }}
                            >
                                <div className="asv-emp-day-logs-table-scroll">
                                    {!selectedDay ? (
                                        <p className={`asv-emp-day-logs-placeholder ${isDarkTheme ? "dark-color" : ""}`}>
                                            {t("dayLogs.clickDayHint")}
                                        </p>
                                    ) : loadingEmployeeMonthLogs ? (
                                        <p className={`asv-emp-day-logs-placeholder ${isDarkTheme ? "dark-color" : ""}`}>
                                            {t("dayLogs.loading")}
                                        </p>
                                    ) : employeeMonthLogsByDay == null ? (
                                        <p className={`asv-emp-day-logs-placeholder ${isDarkTheme ? "dark-color" : ""}`}>
                                            {t("dayLogs.notLoaded")}
                                        </p>
                                    ) : selectedLogs.length === 0 ? (
                                        <p className={`asv-emp-day-logs-placeholder ${isDarkTheme ? "dark-color" : ""}`}>
                                            {t("dayLogs.emptyDay")}
                                        </p>
                                    ) : (
                                        <table className={`asv-emp-day-logs-table ${isDarkTheme ? "asv-emp-day-logs-table--dark" : ""}`}>
                                            <thead>
                                                <tr>
                                                    <th className="asv-emp-day-logs-table-col-action" aria-label={t("dayLogs.editColumnAria")} />
                                                    <th>{t("dayLogs.columns.time")}</th>
                                                    <th className="asv-emp-day-logs-table-col-status" aria-label={t("dayLogs.columns.status")}>
                                                        {t("dayLogs.columns.status")}
                                                    </th>
                                                    <th>{t("dayLogs.columns.attendanceType")}</th>
                                                    <th>{t("dayLogs.columns.userConfirmation")}</th>
                                                    <th className="asv-emp-day-logs-table-col-confirm" aria-label={t("dayLogs.confirmationStatusAria")} />
                                                    <th>{t("dayLogs.columns.location")}</th>
                                                    <th>{t("dayLogs.columns.editedBy")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedLogs.map((log, idx) => {
                                                    const rowKey = getLogRowKey(logsTableKey, log, idx)
                                                    const isEditing = editingLogRowKey === rowKey
                                                    const isFlashing =
                                                        log._id != null && String(log._id) === flashingLogId
                                                    return (
                                                        <tr
                                                            key={rowKey}
                                                            className={`asv-emp-day-log-row${isFlashing ? " asv-emp-day-log-row--flash" : ""}`}
                                                        >
                                                            <td className="asv-emp-day-logs-table-col-action">
                                                                <button
                                                                    type="button"
                                                                    className={`asv-emp-day-log-edit-btn${isEditing ? " asv-emp-day-log-edit-btn--active" : ""}`}
                                                                    aria-label={t("dayLogs.editTimeAria")}
                                                                    aria-expanded={isEditing}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        setEditingLogRowKey((prev) => (prev === rowKey ? null : rowKey))
                                                                    }}
                                                                >
                                                                    <img
                                                                        className={`dv-card-edit-tool-btn ${isDarkTheme ? "dark-filter" : ""}`}
                                                                        src="/cards/Edit.svg"
                                                                        alt=""
                                                                    />
                                                                </button>
                                                            </td>
                                                            <td>{log.vreme ?? "—"}</td>
                                                            <td className="asv-emp-day-logs-table-col-status">
                                                                <LogStatusIcon status={log.status} />
                                                            </td>
                                                            <td>{formatAttendanceTypeCell(log, t)}</td>
                                                            <td>{formatLogConfirmerCell(log, employeesData)}</td>
                                                            <td className="asv-emp-day-logs-table-col-confirm">
                                                                <LogConfirmationStatusIcon log={log} />
                                                            </td>
                                                            <td className="asv-emp-day-logs-table-cell-loc">{formatLogLocationCell(log)}</td>
                                                            <td>{formatLogEditorCell(log, employeesData)}</td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                {totalInsideFormatted != null ? (
                                    <div className="asv-emp-day-logs-table-total" role="status">
                                        <span className="asv-emp-day-logs-table-total-label">{t("dayLogs.total")}</span>
                                        <span className="asv-emp-day-logs-table-total-value">{totalInsideFormatted}</span>
                                    </div>
                                ) : null}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
            {modalContainerRef && (
                <EmployeeDayLogTimeEditPopover
                    open={editingLogRowKey != null}
                    modalContainerRef={modalContainerRef}
                    initialVreme={editingLogContext?.log?.vreme ?? "00:00"}
                    prevVreme={editingLogContext?.prevVreme ?? null}
                    nextVreme={editingLogContext?.nextVreme ?? null}
                    onClose={closeTimeEdit}
                    onSave={handleSaveLogTime}
                />
            )}
            {createPortal(
                <AnimatePresence>
                    {weekTooltip && (
                        <motion.div
                            className={`v-header-button-tooltip asv-emp-week-number-tooltip ${isDarkTheme ? "tooltip-dark" : "tooltip-white"}`}
                            style={{ top: weekTooltip.top, left: weekTooltip.left }}
                            initial={{ opacity: 0, x: -6, y: "-50%" }}
                            animate={{ opacity: 1, x: 0, y: "-50%" }}
                            exit={{ opacity: 0, x: -6, y: "-50%" }}
                            transition={{ duration: 0.16, ease: "easeOut" }}
                        >
                            {t("calendar.weekNumberTitle", { week: weekTooltip.weekNumber })}
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    )
}

export default EmployeeMonthLogsCalendar
